import { useState } from "react";
import { Link } from "wouter";
import {
  useActiveRentals,
  useBackfillOrders,
  useDeviceOperation,
  useEjectRepair,
  type ActiveRental,
} from "../lib/api.js";
import { StatusPill } from "../components/StatusPill.js";

function relTime(ts: number): { label: string; tone: "ok" | "warn" | "bad" } {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60_000);
  let label: string;
  if (mins < 1) label = "just now";
  else if (mins < 60) label = `${mins}m`;
  else if (mins < 60 * 24) label = `${Math.floor(mins / 60)}h ${mins % 60}m`;
  else label = `${Math.floor(mins / (60 * 24))}d`;

  let tone: "ok" | "warn" | "bad" = "ok";
  if (mins >= 60 * 24) tone = "bad";
  else if (mins >= 60 * 4) tone = "warn";
  return { label, tone };
}

function Row({ r, expanded, onToggle }: { r: ActiveRental; expanded: boolean; onToggle: () => void }) {
  const t = relTime(r.borrowedAt);
  const eject = useEjectRepair();
  const op = useDeviceOperation();
  return (
    <>
      <tr
        onClick={onToggle}
        className="cursor-pointer border-b border-zinc-800/60 transition hover:bg-zinc-900/40 last:border-b-0"
      >
        <td className="px-4 py-3 font-mono text-xs text-zinc-200">{r.batteryId}</td>
        <td className="px-4 py-3 font-mono text-xs text-zinc-400">{r.cabinetId}</td>
        <td className="px-4 py-3 font-mono text-xs text-zinc-400">
          {new Date(r.borrowedAt).toLocaleString()}
        </td>
        <td className="px-4 py-3">
          <StatusPill tone={t.tone}>out {t.label}</StatusPill>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={4} className="bg-zinc-950/40 px-4 py-3">
            <div className="flex flex-wrap gap-2">
              <Link href={`/batteries/${r.batteryId}`} className="btn-secondary">
                Battery lifecycle →
              </Link>
              <Link href={`/cabinets/${r.cabinetId}`} className="btn-secondary">
                Open cabinet →
              </Link>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  op.mutate({
                    cabinetId: r.cabinetId,
                    operationType: "report",
                    slotNum: 0,
                    reason: "ops",
                  });
                }}
                disabled={op.isPending}
                className="btn-purple"
              >
                Request kiosk status
              </button>
              {t.tone === "bad" && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    eject.mutate({ batteryId: r.batteryId });
                  }}
                  disabled={eject.isPending}
                  className="btn-danger"
                >
                  Eject (stuck rental)
                </button>
              )}
            </div>
            <p className="mt-2 text-[11px] text-zinc-500">
              Force-return is not exposed by Bajie&apos;s API; use{" "}
              <code className="text-zinc-400">/order/list</code> + close-out flow if you need
              to manually settle. Eject above triggers Bajie&apos;s repair endpoint —
              suitable only for stuck/abandoned rentals.
            </p>
          </td>
        </tr>
      )}
    </>
  );
}

export function ActiveRentals() {
  const { data, isLoading, error } = useActiveRentals();
  const backfill = useBackfillOrders();
  const rows = data ?? [];
  const stuck = rows.filter((r) => Date.now() - r.borrowedAt >= 24 * 3600 * 1000).length;
  const [expandedId, setExpandedId] = useState<string | null>(null);

  return (
    <div className="mx-auto max-w-7xl px-6 py-8">
      <header className="mb-4 flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-50">Active rentals</h1>
          <p className="mt-1 text-sm text-zinc-500">
            Every battery currently borrowed (last <code className="font-mono text-xs text-zinc-400">BATTERY_BORROW_OUT</code> with no subsequent <code className="font-mono text-xs text-zinc-400">BATTERY_IN</code>).
            {stuck > 0 && (
              <> · <span className="text-rose-300">{stuck} stuck &gt; 24h</span></>
            )}
          </p>
        </div>
        <button
          onClick={() => backfill.mutate()}
          disabled={backfill.isPending}
          className="btn-purple"
          title="Pull every completed order from Bajie's /order/list and synthesize BORROW_OUT + BATTERY_IN events for each. Idempotent — safe to re-run."
        >
          {backfill.isPending ? "Backfilling…" : "Backfill order history"}
        </button>
      </header>

      {backfill.data && (
        <div className="mb-4 rounded-md bg-brand-purple/10 px-3 py-2 text-xs text-purple-200 ring-1 ring-brand-purple/30">
          ✓ {backfill.data.totalOrders} orders processed across {backfill.data.pagesPulled} pages
          · {backfill.data.synthesizedBorrows} BORROW_OUT + {backfill.data.synthesizedReturns} BATTERY_IN events synthesized
        </div>
      )}
      {backfill.isError && (
        <div className="mb-4 rounded-md bg-rose-500/10 px-3 py-2 text-xs text-rose-300 ring-1 ring-rose-500/30">
          {(backfill.error as Error).message}
        </div>
      )}

      {error && (
        <div className="card p-6 text-sm text-rose-300">
          Failed to load: {(error as Error).message}
        </div>
      )}

      {isLoading && <div className="card p-10 text-center text-sm text-zinc-500">Loading…</div>}

      {!isLoading && rows.length === 0 && (
        <div className="card p-10 text-center text-sm text-zinc-400">
          No active rentals right now.
        </div>
      )}

      {rows.length > 0 && (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-800/60 text-left text-[11px] uppercase tracking-wider text-zinc-500">
                <th className="px-4 py-3 font-medium">Battery</th>
                <th className="px-4 py-3 font-medium">Cabinet</th>
                <th className="px-4 py-3 font-medium">Borrowed</th>
                <th className="px-4 py-3 font-medium">Duration</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <Row
                  key={r.batteryId}
                  r={r}
                  expanded={expandedId === r.batteryId}
                  onToggle={() =>
                    setExpandedId((prev) => (prev === r.batteryId ? null : r.batteryId))
                  }
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
